import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  BRANCH_ADVANCE_SEARCH_INITIAL_VALUE,
  getAllBranch,
  getBranchFields,
  getFirmNameDdl,
  IBranch,
  IBranchAdvancedSearch,
} from "./BranchListController";
import { IOption, IPagination } from "../../data/AppInterface";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { eyeIcon, filterIcon } from "../../assets/icons/SvgIconList";
import { AdvanceSearch, ICButton, ICTable } from "../../core-component/ui";
import { ORDER_BY } from "../../utils/AppConstants";
import { ICCheckbox, ICDropDown } from "../../core-component/form";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import BranchForm from "./BranchForm";
import { useLocation, useNavigate } from "react-router-dom";

let timer: ReturnType<typeof setTimeout>;

const BranchList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = UseTranslationHook();
  const branchId = location?.state?.id;
  const isDeleted = location?.state?.is_delete;
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [branchList, setBranchList] = useState<IBranch[]>([]);
  const [firmName, setFirmName] = useState<IOption<string>[]>([]);
  const [advanceSearch, setAdvanceSearch] = useState<IBranchAdvancedSearch>(
    BRANCH_ADVANCE_SEARCH_INITIAL_VALUE,
  );
  const [branchToView, setBranchToView] = useState<IBranch>();

  useEffect(() => {
    fetchBranches({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  useEffect(() => {
    fetchFirmName();
  }, []);
  const fetchBranches = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await getAllBranch(
        setBranchList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const customActionView = (item: IBranch) => {
    return (
      <div className="flex justify-center">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setBranchToView(item);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>
      </div>
    );
  };

  const fetchFirmName = async () => {
    setIsLoading(true);
    await getFirmNameDdl(setFirmName);
  };

  useEffect(() => {
    if (location.state) {
      setAdvanceSearch((prev) => ({
        ...prev,
        firmId: branchId,
        sd: isDeleted === BIT_VALUE.One ? BIT_VALUE.One : BIT_VALUE.Zero,
      }));
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);

  const filterView = (
    <div className="w-full flex">
      <ICDropDown
        className="min-w-72"
        placeholder={t("firm_name")}
        selected={advanceSearch.firmId ? advanceSearch.firmId : undefined}
        options={firmName}
        searchable
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            firmId: option.data_value,
          }))
        }
      />

      <ICCheckbox
        label={t("show_deleted")}
        checkboxClassName="ml-4"
        checked={advanceSearch.sd === BIT_VALUE.One}
        onChange={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            sd: option ? BIT_VALUE.One : BIT_VALUE.Zero,
          }))
        }
      />
      <div className="!py-0 ml-4">
        <ICButton
          onClick={() => setAdvanceSearch(BRANCH_ADVANCE_SEARCH_INITIAL_VALUE)}
          className="flex !w-fit"
        >
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        defaultOpen={!!branchId}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchBranches(pagination);
        }}
      />

      <ICTable<IBranch>
        isLoading={isLoading}
        totalFlex="grid grid-cols-14"
        fields={getBranchFields(customActionView, t)}
        data={branchList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchBranches({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchBranches({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchBranches({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />
      {showForm && branchToView && (
        <BranchForm
          handleClose={() => setShowForm(false)}
          branchToView={branchToView}
        />
      )}
    </div>
  );
};

export default BranchList;
